import React, { useEffect, useRef, useState, useCallback } from "react";
import { Tag, X, Plus, Check, ChevronDown, ChevronUp, ExternalLink, FileText, Brain } from "lucide-react";
import KnowledgeBase from "@knowledge/components/KnowledgeBase";
import { KnowledgeProvider, useKnowledge } from "@knowledge/context/KnowledgeContext";
import { useUser } from "@foundation/altzero_base/base/contextapi/UserContext";
import { WorkspaceSwitcher } from "@header/WorkspaceSwitcher";
import { useRevisrLibraryTags } from "../hooks/useRevisrLibraryTags";
import { getDocumentsWithFiles } from "../services/knowledgeTagsService";
import type { DocWithFilepath } from "../services/knowledgeTagsService";
import type { RevisrTag, DocTagsMap } from "../types/knowledgeOverrides";

// ─── Tag Panel ────────────────────────────────────────────────────────────────

type TagPanelProps = {
  docId: string;
  docName: string;
  assignedTags: RevisrTag[];
  availableTags: RevisrTag[];
  onAssign: (tagId: string) => Promise<void>;
  onRemove: (tagId: string) => Promise<void>;
  onCreateAndAssign: (name: string) => Promise<void>;
};

const TagPanel = ({
  docId: _docId,
  docName,
  assignedTags,
  availableTags,
  onAssign,
  onRemove,
  onCreateAndAssign,
}: TagPanelProps) => {
  const [open, setOpen] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [busy, setBusy] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const unassignedTags = availableTags.filter(
    (t) => !assignedTags.some((a) => a.id === t.id)
  );

  const handleAssign = async (tagId: string) => {
    setBusy(true);
    try { await onAssign(tagId); } finally { setBusy(false); }
  };

  const handleRemove = async (tagId: string) => {
    setBusy(true);
    try { await onRemove(tagId); } finally { setBusy(false); }
  };

  const handleCreate = async () => {
    if (!newTagName.trim()) return;
    setBusy(true);
    try {
      await onCreateAndAssign(newTagName.trim());
      setNewTagName("");
    } finally { setBusy(false); }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
        title={`Manage library tags for "${docName}"`}
      >
        <Tag className="h-3.5 w-3.5" />
        <span>{assignedTags.length > 0 ? `${assignedTags.length} tag${assignedTags.length > 1 ? "s" : ""}` : "Add tags"}</span>
        <ChevronDown className="h-3 w-3" />
      </button>

      {open && (
        <div className="absolute left-0 top-6 z-50 w-64 rounded-lg border bg-white shadow-lg">
          <div className="p-3 border-b">
            <p className="text-xs font-medium text-foreground truncate">{docName}</p>
            <p className="text-xs text-muted-foreground mt-0.5">Revisr Library Tags</p>
          </div>
          {assignedTags.length > 0 && (
            <div className="p-2 border-b flex flex-wrap gap-1">
              {assignedTags.map((tag) => (
                <span key={tag.id} className="flex items-center gap-1 text-xs bg-[#33b864]/10 text-[#33b864] px-2 py-0.5 rounded-full">
                  {tag.name}
                  <button onClick={() => handleRemove(tag.id)} disabled={busy} className="hover:text-destructive">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          )}
          {unassignedTags.length > 0 && (
            <div className="max-h-36 overflow-y-auto p-1">
              {unassignedTags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleAssign(tag.id)}
                  disabled={busy}
                  className="w-full flex items-center gap-2 px-2 py-1.5 text-xs text-left hover:bg-muted rounded-md transition-colors"
                >
                  <Check className="h-3 w-3 opacity-0" />
                  <span>{tag.name}</span>
                  <span className="ml-auto text-muted-foreground capitalize">{tag.category}</span>
                </button>
              ))}
            </div>
          )}
          <div className="p-2 border-t flex gap-1">
            <input
              type="text"
              value={newTagName}
              onChange={(e) => setNewTagName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleCreate()}
              placeholder="New tag name..."
              className="flex-1 text-xs border rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-[#33b864]"
            />
            <button
              onClick={handleCreate}
              disabled={busy || !newTagName.trim()}
              className="p-1 rounded bg-[#33b864] text-white hover:bg-[#2da358] disabled:opacity-50"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── Document Files Manager ───────────────────────────────────────────────────

type KBDoc = { id: string; name: string };

const DocumentFilesManager = () => {
  const { state: { documents } } = useKnowledge();
  const { profile } = useUser();
  const [docsWithFiles, setDocsWithFiles] = useState<DocWithFilepath[]>([]);
  const prevKeyRef = useRef<string>("");

  useEffect(() => {
    const orgId: string = (profile as any)?.organisationId ?? (profile as any)?.organization_id ?? '';
    if (!orgId) return;
    const docNames = (documents as KBDoc[] || []).map((d: KBDoc) => d.name).sort().join(",");
    if (!docNames || docNames === prevKeyRef.current) return;
    prevKeyRef.current = docNames;
    getDocumentsWithFiles(orgId)
      .then(setDocsWithFiles)
      .catch((err) => console.error('[DocumentFilesManager] fetch error:', err));
  }, [documents, profile]);

  const filepathByName = new Map(docsWithFiles.map((d: DocWithFilepath) => [d.name, d.filepath]));
  const visible = (documents as KBDoc[] || []).filter((d: KBDoc) => filepathByName.has(d.name));

  if (visible.length === 0) return null;

  return (
    <div className="border-t bg-white/80 backdrop-blur-sm px-4 py-3">
      <div className="flex items-center gap-2 mb-2">
        <FileText className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium text-foreground">Original Files</span>
        <span className="text-xs text-muted-foreground">— view or download the source document</span>
      </div>
      <div className="flex flex-col gap-1.5">
        {visible.map((doc: KBDoc) => {
          const url = filepathByName.get(doc.name)!;
          return (
            <div key={doc.id} className="flex items-center gap-3 py-1">
              <span className="text-xs text-muted-foreground truncate flex-1 max-w-xs">{doc.name}</span>
              <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1 text-xs text-[#33b864] hover:underline">
                <ExternalLink className="h-3.5 w-3.5" />
                View Original
              </a>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─── Tag Manager ─────────────────────────────────────────────────────────────

const TagManager = () => {
  const { state: { documents } } = useKnowledge();
  const { tags: availableTags, isLoading: tagsLoading, assignTags, removeTags, createTag, getTagsByDocs } = useRevisrLibraryTags();
  const [docTagsMap, setDocTagsMap] = useState<DocTagsMap>({});
  const [expanded, setExpanded] = useState(false);
  const prevDocIdsRef = useRef<string>("");

  useEffect(() => {
    const docIds = (documents || []).map((d) => d.id);
    const key = docIds.join(",");
    if (key === prevDocIdsRef.current || docIds.length === 0) return;
    prevDocIdsRef.current = key;
    getTagsByDocs(docIds)
      .then((map) => setDocTagsMap(map))
      .catch((err) => console.error("[TagManager] Failed to load doc tags:", err));
  }, [documents, getTagsByDocs]);

  if (tagsLoading || !documents || documents.length === 0) return null;

  const taggedCount = documents.filter((d) => (docTagsMap[d.id] ?? []).length > 0).length;

  return (
    <div className="border-t bg-white/80 backdrop-blur-sm">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="w-full flex items-center gap-2 px-4 py-2.5 hover:bg-muted/40 transition-colors text-left"
      >
        <Tag className="h-4 w-4 text-muted-foreground flex-shrink-0" />
        <span className="text-sm font-medium text-foreground">Revisr Library Tags</span>
        <span className="text-xs text-muted-foreground ml-1">
          {taggedCount}/{documents.length} tagged
        </span>
        <span className="ml-auto text-muted-foreground">
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </span>
      </button>
      {expanded && (
        <div className="px-4 pb-3 max-h-48 overflow-y-auto flex flex-col gap-1.5">
          {documents.map((doc) => (
            <div key={doc.id} className="flex items-center gap-3 py-1">
              <span className="text-xs text-muted-foreground truncate flex-1 max-w-xs">{doc.name}</span>
              <TagPanel
                docId={doc.id}
                docName={doc.name}
                assignedTags={docTagsMap[doc.id] ?? []}
                availableTags={availableTags}
                onAssign={async (tagId) => {
                  await assignTags({ doc_id: doc.id, tag_ids: [tagId] });
                  const updated = await getTagsByDocs([doc.id]);
                  setDocTagsMap((prev) => ({ ...prev, ...updated }));
                }}
                onRemove={async (tagId) => {
                  await removeTags({ doc_id: doc.id, tag_ids: [tagId] });
                  const updated = await getTagsByDocs([doc.id]);
                  setDocTagsMap((prev) => ({ ...prev, ...updated }));
                }}
                onCreateAndAssign={async (name) => {
                  const tag = await createTag({ name, category: "general" });
                  await assignTags({ doc_id: doc.id, tag_ids: [tag.id] });
                  const updated = await getTagsByDocs([doc.id]);
                  setDocTagsMap((prev) => ({ ...prev, ...updated }));
                }}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ─── KB Sidebar ───────────────────────────────────────────────────────────────

const KBSidebar = () => (
  <aside
    className="w-64 shrink-0 flex flex-col relative overflow-hidden"
    style={{ background: '#132321', borderRight: '1px solid rgba(51,184,100,0.15)' }}
  >
    {/* Dot-grid texture */}
    <div
      className="absolute inset-0 pointer-events-none opacity-[0.03]"
      style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)',
        backgroundSize: '24px 24px',
      }}
    />
    {/* Top green accent line */}
    <div className="h-[2px] w-full flex-shrink-0" style={{ background: 'linear-gradient(90deg, #33b864, transparent)' }} />

    {/* Workspace Switcher */}
    <div className="pt-3 relative z-10">
      <WorkspaceSwitcher />
    </div>

    {/* Section header */}
    <div className="px-5 py-4 relative z-10" style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
      <div className="flex items-center gap-2.5">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ background: 'rgba(51,184,100,0.15)' }}
        >
          <Brain className="w-4 h-4" style={{ color: '#33b864' }} />
        </div>
        <div>
          <h2 className="text-sm font-bold" style={{ color: '#ffffff', letterSpacing: '-0.01em' }}>Knowledge Base</h2>
          <p className="text-[11px]" style={{ color: 'rgba(255,255,255,0.45)' }}>AI document management</p>
        </div>
      </div>
    </div>
  </aside>
);

// ─── KnowledgeBaseContent (inner — has KnowledgeProvider context) ─────────────

const KnowledgeBaseContent = () => (
  <div className="flex flex-col h-full flex-1 min-w-0">
    <div className="flex-1 min-h-0 overflow-y-auto">
      <KnowledgeBase />
    </div>
    <DocumentFilesManager />
    <TagManager />
  </div>
);

// ─── KnowledgeBaseWrapper (exported) ─────────────────────────────────────────
// Uses a `key` on KnowledgeProvider to force full remount on workspace change.
// This ensures all internal useEffects re-run and data is reloaded for the new
// active workspace without needing to touch the plugin's own code.

export const KnowledgeBaseWrapper = () => {
  const [workspaceKey, setWorkspaceKey] = useState(0);

  const handleWorkspaceChange = useCallback(() => {
    // Incrementing key forces React to fully unmount + remount KnowledgeProvider
    // and KnowledgeBase, which re-runs all data fetching for the new workspace.
    setWorkspaceKey((k) => k + 1);
  }, []);

  // Also listen to the global workspace-changed event (fired by WorkspaceSwitcher)
  useEffect(() => {
    const handler = () => handleWorkspaceChange();
    window.addEventListener("workspace-changed", handler);
    return () => window.removeEventListener("workspace-changed", handler);
  }, [handleWorkspaceChange]);

  return (
    <div className="flex h-[calc(100vh-4rem)] bg-white">
      <KBSidebar />
      <KnowledgeProvider key={workspaceKey}>
        <KnowledgeBaseContent />
      </KnowledgeProvider>
    </div>
  );
};
